import { html, css } from "lit";
import { customElement, property, query, queryAll } from "lit/decorators.js";
import { ModuleCode } from "@reporting/rp-types/accounting";
import { msg } from "@omegagrid/localize";
import { map } from "lit/directives/map.js";
import { ContextFilter, ContextFilterItem } from "@reporting/rp-types/analytics";
import { DimTreeAdapter } from "../../adapters/dimAdapter";
import { DimensionDefinition, ModuleDefinition } from "@reporting/rp-types/definition";
import { Button, dom } from "@omegagrid/core";
import { TreeEvent, TreeMenu, TreeSource } from "@omegagrid/tree";
import { BaseSection } from "./sectionBase";
import { createRef, ref } from "lit/directives/ref.js";
import { isStandardDimension, getContextYears } from "../../common";
import { DimAttributeTreeAdapter } from "../../adapters";

@customElement('rp-fsection-multidimensions')
export class MultiDimensionSection extends BaseSection {

	static styles = [...BaseSection.styles, css`
		og-button.add {
			border: 1px dashed var(--og-accent-color);
			width: 100%;
		}

		.row {
			display: flex;
			align-items: center;
		}

		.row og-tree-dropdown {
			flex: 1;
			border: 1px solid var(--og-colortype-accent-border-color);
			background-color: var(--og-accent-color-alpha-30);
			color: var(--og-text-color-3);
		}

		.row og-button {
			flex: 0;
		}
	`];

	/** Modules whose dimensions can be combined in the common filters */
	@property({type: Array})
	modules: ModuleDefinition[];

	@query('og-button.add')
	addButton!: Button;

	@queryAll('og-button.remove')
	removeButtons!: NodeListOf<Button>;

	private addMenuRef = createRef<TreeMenu>();
	get addMenu() { return this.addMenuRef?.value }

	private _filters: ContextFilter[];
	get filters() {
		if (this._filters) return this._filters;
		if (!this.context) return [];
		this._filters = this.context.filters?.filter(f => isStandardDimension(f.dim)) as ContextFilter[] ?? [];
		return this._filters;
	}

	getModule(code: string) {
		return this.modules?.find(m => m.module == code);
	}

	private _dimensionMaps = new Map<string, Map<string, DimensionDefinition>>();
	getDimensionDef(module: string, dimNum: string) {
		if (!this._dimensionMaps.has(module)) {
			const dimMap = new Map<string, DimensionDefinition>();
			for (const dim of this.getModule(module)?.dimensions || []) {
				dimMap.set(dim.dim_num as string, dim);
			}
			this._dimensionMaps.set(module, dimMap);
		}
		return this._dimensionMaps.get(module).get(dimNum);
	}

	private adapters = new Map<string, DimTreeAdapter>;
	getAdapter(module: string, dimNum: string) {
		const key = `${module}:${dimNum}`;
		if (!this.adapters.has(key) && this.service) {
			this.adapters.set(key, new DimTreeAdapter(module as ModuleCode, dimNum));
		}

		const adapter = this.adapters.get(key);
		if (this.context?.filters) {
			adapter.years = getContextYears(this.context, {monthOffset: 0}); // TODO: use correct fiscal year definition
		}
		return adapter;
	}

	resetItems() {
		this.adapters.clear();
		this.requestUpdate();
	}

	willUpdate(props: Map<PropertyKey, unknown>) {
		super.willUpdate(props);
		if (props.has('analyticsClient') || props.has('modules')) {
			this.adapters.clear();
			this._dimensionMaps.clear();
		}
		this._filters = null;
	}

	get addMenuItems() {
		const items: TreeSource = [];
		// with a single module the root level is omitted
		const showModules = this.modules?.length > 1;
		this.modules?.forEach((module, moduleIndex) => {
			if (showModules) {
				items.push({
					key: moduleIndex.toString(),
					value: module.name,
					icon: {icon: 'cube', color: module.color},
				});
			}
			// only numeric (standard) dimensions can be combined across modules
			module.dimensions?.forEach((dim, dimIndex) => {
				if (!isStandardDimension(dim.dim_num as string)) return;
				items.push({
					key: `${moduleIndex}_${dimIndex}`,
					value: dim.description,
					level: showModules ? 1 : 0,
					icon: 'hexagon',
				});
				dim.properties?.forEach((prop, i) => items.push({
					key: `${moduleIndex}_${dimIndex}_${i}`,
					value: prop.name,
					level: showModules ? 2 : 1,
					icon: 'brackets-curly',
				}));
			});
		});
		return items;
	}

	updated() {
		this.addMenu?.setSourceData(this.addMenuItems, {
			selectMode: 'single',
			checkboxes: false
		});
	}

	disconnectedCallback(): void {
		this.addMenu?.remove();
	}

	_onFilterChange = (e: TreeEvent, index: number) => {
		const filter = this.filters[index];
		if (!filter) return;
		filter.items = e.component.selection.serialize() as undefined as ContextFilterItem[];
		if (filter.items.length == 0) dom.showElement(this.removeButtons[index]);
		else dom.hideElement(this.removeButtons[index]);

		this.updateFilters();
	}

	openAddMenu() {
		dom.setSize(this.addMenu, {w: this.addButton.offsetWidth});
		this.addMenu.open(this.addButton);
	}

	updateFilters(refresh?: boolean) {
		this.updateContext('filters', [
			...(this.context.filters || []).filter(f => !isStandardDimension(f.dim)) as ContextFilter[],
			...this.filters
		]);
		if (refresh) this.requestUpdate();
	}

	addFilter(key: string) {
		const parts = key.split('_');
		if (parts.length < 2) return; // module root level is not selectable

		const module = this.modules[parseInt(parts[0])];
		const dim = module.dimensions[parseInt(parts[1])];
		const attrIndex = parts[2] ? parseInt(parts[2]) : -1;

		let dimNum = dim.dim_num as string;
		if (attrIndex > -1) dimNum = `${dimNum}:${dim.properties[attrIndex].key}`;

		this.filters.push({
			dim: dimNum,
			items: [],
			module: module.module
		});

		this.updateFilters(true);
	}

	removeFilter(index: number) {
		this.filters.splice(index, 1);
		this.updateFilters(true);
	}

	render = () => html`
		${map(this.filters, (filter, index) => {
			const parts = filter.dim.split(':', 2);
			const dimNum = parts[0];
			const attribute = parts[1];
			const moduleCode = filter.module || this.modules?.[0]?.module;
			const dimDef = this.getDimensionDef(moduleCode, dimNum);
			const moduleSuffix = this.modules?.length > 1 ? ` (${moduleCode})` : '';
			return html`
				<div class="row" data-index="${index}">
					<og-tree-dropdown
						color="transparent"
						.caption="${attribute
							? `${attribute} (${dimDef?.description})${moduleSuffix}`
							: `${dimDef?.description}${moduleSuffix}`
						}"
						.adapter="${attribute
							? new DimAttributeTreeAdapter(moduleCode as ModuleCode, dimNum, attribute)
							: this.getAdapter(moduleCode, dimNum)
						}"
						.value="${filter.items}"
						@change="${(e: TreeEvent) => this._onFilterChange(e, index)}"
						cross>
					</og-tree-dropdown>

					<og-button
						class="remove"
						style="flex: 0 0 20px; display: ${filter?.items?.length ? 'none' : 'block'}"
						.icon="${{icon: 'trash', color: 'red'}}"
						color="tranparent"
						@click="${() => this.removeFilter(index)}">
					</og-button>
				</div>
			`;
		})}

		${this.modules?.length ? html`
			<div class="row" style="display: block; margin-bottom: 0">
				<og-button
					caret="down"
					class="add"
					color="transparent"
					@click="${() => this.openAddMenu()}">
					<div slot="content">+ ${msg('Add dimension filter')}</div>
				</og-button>
			</div>
		` : null}

		<og-tree-menu ${ref(this.addMenuRef)}
			color="transparent"
			style="display: none; z-index: 1000"
			maxHeight="200"
			@select="${(e: TreeEvent) => this.addFilter(e.node.key as string)}">
		</og-tree-menu>
	`;

}
